import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Input, Row, Table } from 'reactstrap';
import { toast } from "react-toastify";
import _ from 'lodash';
import { FilterSelect, ServiceThreadSpinner } from '.';
import { api, filterHelpers, rwoMachines } from '../utils';

const { ADD_COUNT_MESSAGE, MAX_MACHINES_PER_ADD, addedMachinesMessage, parseAddCount } = rwoMachines;
const INACTIVE_SUFFIX = " (inactive)";

export default function ManageRwoMachines() {
   const ALL_OPT = { value: 0, label: "-- Select --" };
   const EDIT_MODEL = { id: 0, name: "", number: 0, isActive: false };
   const [active, setActive] = useState(false);
   const [addCount, setAddCount] = useState("");
   const [adding, setAdding] = useState(false);
   const [numList, setNumList] = useState([]);
   const [rwoList, setRwoList] = useState([]);
   const [rwoNum, setRwoNum] = useState(0);
   const [rwoType, setRwoType] = useState(EDIT_MODEL);
   const [filters, setFilters] = useState([
      { filterName: "rwoNum", value: ALL_OPT },
      { filterName: "rwoType", value: ALL_OPT }
   ]);
   const [spinRwoMach, setSpinRwoMach] = useState(true);
   const selectedTypeId = useRef(0); // the type whose machine list is wanted - a slower earlier load is ignored

   useEffect(() => {
      runQuery();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   // The type's REAL machine rows, active and inactive, so an inactive machine can be reactivated here.
   function loadMachines(typeId) {
      selectedTypeId.current = typeId;
      setNumList([ALL_OPT]); // never leave the previous type's numbers showing while this one loads
      setSpinRwoMach(true);
      return api
         .fetch(`Repair/GetRwoMachines/${typeId}`)
         .then((response) => {
            if (selectedTypeId.current !== typeId) return; // the user moved to another type - this answer is stale
            if (api.toastFailure(response, "The machines for that type could not be loaded.")) {
               setNumList([]); // hides the number list, so a failed load is not read as "No machines yet"
               return;
            }
            setNumList(_.concat([ALL_OPT],
               _.map(response.data.machines, (m) => ({
                  value: m.machineNumber,
                  label: m.isActive ? `${m.machineNumber}` : `${m.machineNumber}${INACTIVE_SUFFIX}`,
                  isActive: m.isActive
               }))));
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => setSpinRwoMach(false));
   }

   function runQuery() {
      setSpinRwoMach(true);
      api
         .fetch(`Repair/GetRwoActiveMachineTypes`)
         .then((response) => {
            if (response.data.success) {
               const mtList = _.concat([ALL_OPT],
                  _.map(response.data.machines, (mt) => ({
                     value: mt.machineId,
                     label: mt.machineName
                  })));
               return { mtList: mtList };
            } else {
               toast.error(response.data.message);
            }
         })
         .then(({ mtList }) => {
            setRwoList(mtList);
            setNumList([ALL_OPT]); // Default selection, only filled in when a machine is selected
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setRwoType(EDIT_MODEL);
            setRwoNum(0);
            const typ = _.find(rwoList, (x) => x.value === 0);
            const num = _.find(numList, (x) => x.value === 0);
            let newFilters = filterHelpers.getFilters(filters, {
               filterName: "rwoType",
               value: typ,
            });
            newFilters = filterHelpers.getFilters(newFilters, {
               filterName: "rwoNum",
               value: num,
            });
            setFilters(newFilters);
            setSpinRwoMach(false);
         });
   }

   function onNumChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);
      setRwoNum(newFilterValue.value);

      const mach = _.find(numList, (x) => x.value === newFilterValue.value);
      setActive(mach.isActive);

      let changes = Object.assign({}, rwoType);
      changes.number = newFilterValue.value;
      changes.isActive = mach.isActive;
      setRwoType(changes);
   }

   function onTypeChange(newFilterValue) {
      if (newFilterValue.value === 0) {
         // Reset the selected machine type
         selectedTypeId.current = 0; // discards a load still in flight for the previous type
         setRwoType(EDIT_MODEL);
         setNumList([ALL_OPT]);
      }
      else { // Get the selected machine type and its machines
         let changes = Object.assign({}, EDIT_MODEL);
         changes.id = newFilterValue.value;
         changes.name = newFilterValue.label;
         setRwoType(changes);
         loadMachines(newFilterValue.value);
      }
      setRwoNum(0);
      setAddCount("");

      // Now set up the filters
      const mach = _.find(rwoList, (x) => x.value === newFilterValue.value);
      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "rwoType",
         value: mach,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "rwoNum",
         value: ALL_OPT,
      });
      setFilters(newFilters);
   }

   function toggleActive() {
      if (!rwoType || rwoType.id === 0) {
         toast.error("Please select a machine");
         return;
      }
      if (rwoType.number === 0) {
         toast.error("Please select a machine number");
         return;
      }

      setSpinRwoMach(true);
      api.post(`Repair/ToggleRwoMachineActive/${rwoType.id}/${rwoType.number}/${!rwoType.isActive}`)
         .then((response) => {
            if (response.data.success) {
               toast.success("Active flag updated successfully!")
               runQuery();
            } else {
               toast.error(response.data.message);
               setSpinRwoMach(false);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            setSpinRwoMach(false);
         });
   }

   function addMachines() {
      if (adding) return; // one add at a time - a second click must not mint more machines
      if (!rwoType || rwoType.id === 0) {
         toast.error("Please select a machine");
         return;
      }
      const count = parseAddCount(addCount);
      if (!count) {
         toast.error(ADD_COUNT_MESSAGE);
         return;
      }

      const typeId = rwoType.id;
      const typeName = rwoType.name;
      setAdding(true);
      api.post(`Repair/AddMachines/${typeId}/${count}`)
         .then((response) => {
            if (api.toastFailure(response, "The machines could not be added.")) return;
            toast.success(addedMachinesMessage(response.data.numbers, typeName));
            setAddCount("");
            setRwoNum(0);
            setFilters((current) => filterHelpers.getFilters(current, { filterName: "rwoNum", value: ALL_OPT }));
            loadMachines(typeId);
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => setAdding(false));
   }

   return (
      <Container fluid>
         <Row>
            <Col>
               <Fragment>
                  <ServiceThreadSpinner on={spinRwoMach}>
                     <Row className="px-2 py-2">
                        <Col xxl={{ offset: 0, size: 12 }}>
                           {rwoList && rwoList.length ? (
                              <Table className="transparent-background my-0">
                                 <tbody>
                                    <tr>
                                       <td colSpan={3} className="transparent-background data-cell noWrap h6">
                                          Activate or add machines
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6 width115px">
                                          Machine:
                                       </td>
                                       <td className="transparent-background width500px">
                                          <FilterSelect
                                             filterName="rwoType"
                                             options={rwoList}
                                             value={filterHelpers.getValue(filters, "rwoType")}
                                             disabled={adding}
                                             onChangeCallback={e => onTypeChange(e)}
                                          />
                                       </td>
                                       <td className="transparent-background data-cell"></td>
                                    </tr>
                                    {numList && numList.length ? (
                                       <tr>
                                          <td className="transparent-background data-cell noWrap h6 width115px">
                                             Machine #:
                                          </td>
                                          <td className="transparent-background width500px">
                                             <FilterSelect
                                                filterName="rwoNum"
                                                options={numList}
                                                value={filterHelpers.getValue(filters, "rwoNum")}
                                                onChangeCallback={e => onNumChange(e)}
                                             />
                                          </td>
                                          <td className="transparent-background data-cell">
                                             {rwoType.id > 0 && numList.length <= 1 ? `No machines yet for ${rwoType.name}.` : null}
                                          </td>
                                       </tr>
                                    ) : null}
                                    {rwoNum && rwoNum > 0 ? (
                                       <tr>
                                          <td className="transparent-background data-cell noWrap h6 width115px">
                                             Active: {active ? "Yes" : "No"}
                                          </td>
                                          <td className="transparent-background data-cell">
                                             <Button title="Remove" className="shredButton" onClick={() => toggleActive()} >
                                                Toggle Active Flag
                                             </Button>
                                          </td>
                                          <td className="transparent-background data-cell"></td>
                                       </tr>
                                    ) : null}
                                    {rwoType.id > 0 ? (
                                       <tr>
                                          <td className="transparent-background data-cell noWrap h6 width115px">
                                             How many:
                                          </td>
                                          <td className="transparent-background width500px">
                                             <Input
                                                type="number"
                                                min={1}
                                                max={MAX_MACHINES_PER_ADD}
                                                value={addCount}
                                                disabled={adding}
                                                onChange={(e) => setAddCount(e.target.value)}
                                             />
                                          </td>
                                          <td className="transparent-background data-cell">
                                             <Button className="shredButton" disabled={adding} onClick={() => addMachines()}>
                                                Add machines
                                             </Button>
                                          </td>
                                       </tr>
                                    ) : null}
                                 </tbody>
                              </Table>
                           ) : null}
                        </Col>
                     </Row>
                  </ServiceThreadSpinner>
               </Fragment>
            </Col>
         </Row>
      </Container >
   );
}
